import React, { useMemo, useState } from "react"
import Button from "@react/shared/ui/Button"
import Popover from "./Popover"
import AssignmentFields, { fieldClass, fieldsToAttrs, initialFields, projectLabel } from "./AssignmentFields"
import { addDays, datesBetween, formatLong, formatShort, isWeekend, parseISO } from "../lib/dates"

const fridayOf = iso => addDays(iso, (5 - parseISO(iso).getUTCDay() + 7) % 7)

const CreatePopover = ({ rect, target, board, defaultProjectId, isDayOff, onSubmit, onClose }) => {
  const byProject = target.projectId != null
  const [fields, setFields] = useState(() => ({
    ...initialFields(board.settings),
    projectId: byProject ? target.projectId : (defaultProjectId || ""),
  }))
  const [userId, setUserId]   = useState(byProject ? "" : target.userId)
  const [repeat, setRepeat]   = useState(false)
  const [until, setUntil]     = useState(() => fridayOf(target.dates[0]))
  const [saving, setSaving]   = useState(false)

  const project = board.projects.find(item => item.id === Number(fields.projectId))

  const dates = useMemo(() => {
    if (!repeat || target.dates.length > 1 || until <= target.dates[0]) return target.dates
    return datesBetween(target.dates[0], until)
      .filter(date => date === target.dates[0] || (!isWeekend(date) && !isDayOff(userId || null, date)))
  }, [repeat, until, target.dates, userId, isDayOff])

  const submit = event => {
    event.preventDefault()
    if (!project || !userId || saving) return
    setSaving(true)
    onSubmit({ projectId: project.id, userIds: [Number(userId)], dates, attrs: fieldsToAttrs(fields) })
    onClose()
  }

  const title = byProject
    ? projectLabel(board.projects.find(item => item.id === target.projectId) || {})
    : target.userName
  const subtitle = target.dates.length > 1
    ? I18n.t("project_planning.create.days", { count: target.dates.length, from: formatShort(target.dates[0]), to: formatShort(target.dates[target.dates.length - 1]) })
    : formatLong(target.dates[0])

  return (
    <Popover
      rect={rect}
      width={340}
      title={title}
      subtitle={subtitle}
      onClose={onClose}
      footer={(
        <>
          <Button variant="ghost" size="sm" onClick={onClose}>{I18n.t("project_planning.actions.cancel")}</Button>
          <Button variant="primary" size="sm" className="ml-auto" disabled={!project || !userId || saving} onClick={submit}>
            {dates.length > 1
              ? I18n.t("project_planning.create.submit_many", { count: dates.length })
              : I18n.t("project_planning.create.submit")}
          </Button>
        </>
      )}
    >
      <form onSubmit={submit} className="flex flex-col gap-3">
        {byProject && (
          <div className="flex flex-col gap-1">
            <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{I18n.t("project_planning.fields.person")}</span>
            <select autoFocus className={fieldClass} value={userId} onChange={event => setUserId(event.target.value)}>
              <option value="" disabled>{I18n.t("project_planning.fields.choose_person")}</option>
              {board.users.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}
            </select>
          </div>
        )}

        <AssignmentFields
          fields={fields}
          onChange={setFields}
          projects={board.projects}
          settings={board.settings}
          showProject={!byProject}
          autoFocus={!byProject}
        />

        {target.dates.length === 1 && (
          <div className="flex items-center gap-2 pt-1">
            <label className="inline-flex items-center gap-2 text-[12.5px] text-slate-700 cursor-pointer select-none">
              <input type="checkbox" className="rounded border border-slate-300 text-brand-500 focus:ring-brand-500/30" checked={repeat} onChange={event => setRepeat(event.target.checked)} />
              {I18n.t("project_planning.create.repeat_until")}
            </label>
            <input
              type="date"
              className={`${fieldClass} w-auto flex-1`}
              disabled={!repeat}
              min={target.dates[0]}
              value={until}
              onChange={event => setUntil(event.target.value)}
            />
          </div>
        )}
        {repeat && dates.length > 1 && (
          <p className="text-[11.5px] text-slate-500 -mt-1">
            {I18n.t("project_planning.create.repeat_hint", { count: dates.length })}
          </p>
        )}
        <button type="submit" className="hidden" />
      </form>
    </Popover>
  )
}

export default CreatePopover
